/** @format */
import {ReactNode, useCallback, useEffect, useRef, useState} from 'react';

import {Tooltip} from '@mui/material';

import {
  Root,
  Content,
  Header,
  HeaderText,
  Chip,
  Chips,
  ChipsColumns,
  DescriptionBox,
  DescriptionText,
  HideButton,
  ButtonsBox,
  HeaderWithIcon,
  HighlightBg,
  AlertTooltip,
  AlertTooltipButtonsBox,
  SimpleButtonWrap,
} from './styles';
import {HotspotVariant} from '../../types';
import {BgImage, SetupButton, SubHeaderChip} from '../../styles';
import {HotspotKey} from '../../../../types';
import {ALERT_MEDIAPLAN_KEY, ALERT_SENSORS_KEY, HOTSPOT_ALERT_DELAY} from '../../../../constants';

interface Props {
  variant: HotspotVariant;
  bgImage: string;
  onHide: (key: HotspotKey) => void;
  onSetup: () => void;
  isHighlight: boolean;
  bgOpacity: number;
  hotspotKey: HotspotKey;
  content: {
    headerText: string;
    HeaderIcon?: ReactNode;
    subHeaderText?: string;
    description: string[];
    chips: string[];
    RightColumnComponent?: ReactNode;
    buttonText: string;
    AlertComponent?: ReactNode;
  };
}

const HotspotBig = ({
  variant,
  bgImage,
  content,
  onHide,
  onSetup,
  isHighlight,
  bgOpacity,
  hotspotKey,
}: Props) => {
  const {
    headerText,
    subHeaderText,
    HeaderIcon,
    RightColumnComponent,
    chips,
    description,
    buttonText,
    AlertComponent,
  } = content;

  const [openAlert, setOpenAlert] = useState<boolean>(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleOpenAlert = useCallback(() => {
    setOpenAlert(true);
  }, []);

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  const handleHide = () => {
    handleCloseAlert();
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    onHide(hotspotKey);
  };

  const handleHideWithAlert = () => {
    handleHide();
    switch (hotspotKey) {
      case 'mediaplan': {
        localStorage.setItem(ALERT_MEDIAPLAN_KEY, 'true');
        break;
      }
      case 'sensors': {
        localStorage.setItem(ALERT_SENSORS_KEY, 'true');
        break;
      }
    }
  };

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      if (AlertComponent) {
        handleOpenAlert();
      }
    }, HOTSPOT_ALERT_DELAY);

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [AlertComponent, handleOpenAlert]);

  const isMediaplanHide = localStorage.getItem(ALERT_MEDIAPLAN_KEY) === 'true';
  const isSensorsHide = localStorage.getItem(ALERT_SENSORS_KEY) === 'true';
  const isRenderTooltip =
    (hotspotKey === 'mediaplan' && !isMediaplanHide) ||
    (hotspotKey === 'sensors' && !isSensorsHide);

  return (
    <Root>
      <Content>
        <Header>
          <HeaderWithIcon>
            {HeaderIcon}
            <HeaderText>{headerText}</HeaderText>
          </HeaderWithIcon>
          {subHeaderText ? <SubHeaderChip>{subHeaderText}</SubHeaderChip> : null}
        </Header>

        <DescriptionBox>
          {description.map((text, index) => (
            <DescriptionText key={index}>{text}</DescriptionText>
          ))}
        </DescriptionBox>

        <ChipsColumns>
          <Chips>
            {chips.map((chip, index) => (
              <Chip key={index} isHighlight={isHighlight}>
                {chip}
              </Chip>
            ))}
          </Chips>

          {RightColumnComponent}
        </ChipsColumns>

        <ButtonsBox variant={variant}>
          <HideButton onClick={handleHide} customVariant={variant}>
            Скрыть
          </HideButton>

          <Tooltip
            open={openAlert}
            title={
              AlertComponent &&
              isRenderTooltip && (
                <AlertTooltip>
                  {AlertComponent}
                  <AlertTooltipButtonsBox>
                    <SimpleButtonWrap onClick={handleHideWithAlert}>Скрыть</SimpleButtonWrap>
                    <SetupButton onClick={onSetup} customVariant={variant}>
                      {buttonText}
                    </SetupButton>
                  </AlertTooltipButtonsBox>
                </AlertTooltip>
              )
            }
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: 'transparent',
                },
              },
            }}
          >
            <SetupButton onClick={onSetup} customVariant={variant}>
              {buttonText}
            </SetupButton>
          </Tooltip>
        </ButtonsBox>
      </Content>

      {isHighlight && <HighlightBg />}

      <BgImage src={bgImage} alt='' sx={{opacity: bgOpacity}} />
    </Root>
  );
};

export {HotspotBig};
